import { Routes, Route } from 'react-router-dom';
import { AppShell } from './app/AppShell';
import { ProtectedRoute } from './app/ProtectedRoute';
import { useAuth } from './app/AuthContext';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminBootstrap from './pages/AdminBootstrap';
import AdminContributions from './pages/AdminContributions';
import AdminOwnership from './pages/AdminOwnership';
import AdminProfitEntry from './pages/AdminProfitEntry';
import AdminProfitAllocations from './pages/AdminProfitAllocations';
import AdminSettlementOverview from './pages/AdminSettlementOverview';
import AllocationHistory from './pages/AllocationHistory';
import { Withdrawals } from './pages/Withdrawals';
import { AdminWithdrawals } from './pages/AdminWithdrawals';
import { AdminAuditLogs } from './pages/AdminAuditLogs';
import { AdminOverview } from './pages/AdminOverview';
import { AdminUsers } from './pages/AdminUsers';
import { AdminUserDetail } from './pages/AdminUserDetail';
import { AdminRequests } from './pages/AdminRequests';
import { AdminTreasury } from './pages/AdminTreasury';
import AdminReconciliation from './pages/AdminReconciliation';
import { InviteStatusPage } from './pages/InviteStatusPage';
import { Settings } from './pages/Settings';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { Error403 } from './pages/Error403';
import { Error404 } from './pages/Error404';
import './App.css';

function App() {
  const { isAdmin } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/invite/accept" element={<AcceptInvite />} />
      <Route path="/invite/status" element={<InviteStatusPage />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* S28: Password reset routes */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Error pages */}
      <Route path="/403" element={<Error403 />} />
      <Route path="/404" element={<Error404 />} />

      {/* Protected routes - Dashboard at root with conditional sidebar for admins */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell showSider={isAdmin}>
              <Dashboard />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* Admin routes with sidebar */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminOverview />
            </AppShell>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/bootstrap"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminBootstrap />
            </AppShell>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/contributions"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminContributions />
            </AppShell>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/ownership"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminOwnership />
            </AppShell>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/profit-entry"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminProfitEntry />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/profits/:id/allocations"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminProfitAllocations />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settlements"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminSettlementOverview />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* User history route */}
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <AppShell showSider={isAdmin}>
              <AllocationHistory />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* User settings route */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppShell showSider={isAdmin}>
              <Settings />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S22: User withdrawal requests */}
      <Route
        path="/withdrawals"
        element={
          <ProtectedRoute>
            <AppShell showSider={isAdmin}>
              <Withdrawals />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S23: Admin withdrawal queue */}
      <Route
        path="/admin/withdrawals"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminWithdrawals />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S24: Admin audit logs */}
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminAuditLogs />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S26: Admin users management */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminUsers />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminUserDetail />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S41/S43: Admin account requests (closures & reopens) */}
      <Route
        path="/admin/requests"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminRequests />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S46: Admin treasury / company ledger */}
      <Route
        path="/admin/treasury"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminTreasury />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S-F4: Admin reconciliation */}
      <Route
        path="/admin/reconciliation"
        element={
          <ProtectedRoute adminOnly>
            <AppShell showSider>
              <AdminReconciliation />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* S27: Catch-all 404 route */}
      <Route path="*" element={<Error404 />} />
    </Routes>
  );
}

export default App;
